const hasCountValue = (object, key) =>
  object &&
  Object.prototype.hasOwnProperty.call(object, key) &&
  object[key] !== undefined &&
  object[key] !== null;

const sanitizeCount = (count) => {
  const parsedCount = parseInt(count || 0, 10);
  return Number.isNaN(parsedCount) || parsedCount < 0 ? 0 : parsedCount;
};

const sanitizeInboxCounts = (inboxCounts, fallback = {}) => {
  if (!inboxCounts || typeof inboxCounts !== 'object') {
    return fallback || {};
  }

  return Object.entries(inboxCounts).reduce((acc, [inboxId, count]) => {
    acc[inboxId] = sanitizeCount(count);
    return acc;
  }, {});
};

const existingCount = (state, key) => sanitizeCount(state && state[key]);

const existingFilteredAllCount = (state) => {
  const count = state ? state.filteredAllCount : null;
  return count === undefined || count === null ? null : sanitizeCount(count);
};

const ASSIGNEE_TAB_COUNT_KEYS = {
  me: 'mineCount',
  unassigned: 'unAssignedCount',
};

const ASSIGNEE_TAB_ITEM_COUNT_KEYS = {
  ...ASSIGNEE_TAB_COUNT_KEYS,
  all: 'allCount',
};

const buildAssigneeTabItems = ({ stats = {}, t = (key) => key } = {}) =>
  Object.keys(ASSIGNEE_TAB_ITEM_COUNT_KEYS).map((key) => {
    const count = stats[ASSIGNEE_TAB_ITEM_COUNT_KEYS[key]] || 0;
    return {
      key,
      name:
        typeof t === 'function'
          ? t(`CHAT_LIST.ASSIGNEE_TYPE_TABS.${key}`)
          : `CHAT_LIST.ASSIGNEE_TYPE_TABS.${key}`,
      count,
    };
  });

const resolveSearchConversationTotal = ({
  searchResultsTotal,
  searchResultsLength,
} = {}) => {
  if (searchResultsTotal !== undefined && searchResultsTotal !== null) {
    return sanitizeCount(searchResultsTotal);
  }
  return sanitizeCount(searchResultsLength);
};

const resolveConversationListTotal = ({
  activeAssigneeTab,
  isSearchModeActive = false,
  searchResultsLength = 0,
  searchResultsTotal,
  stats = {},
} = {}) => {
  if (isSearchModeActive) {
    return resolveSearchConversationTotal({
      searchResultsTotal,
      searchResultsLength,
    });
  }

  const tabCountKey = ASSIGNEE_TAB_COUNT_KEYS[activeAssigneeTab];
  if (tabCountKey && hasCountValue(stats, tabCountKey)) {
    return sanitizeCount(stats[tabCountKey]);
  }

  if (hasCountValue(stats, 'filteredAllCount')) {
    return sanitizeCount(stats.filteredAllCount);
  }

  return sanitizeCount(stats.allCount);
};

const buildConversationStatsUpdate = (state = {}, meta = {}) => {
  let filteredAllCount = existingFilteredAllCount(state);
  if (hasCountValue(meta, 'filtered_all_count')) {
    filteredAllCount = sanitizeCount(meta.filtered_all_count);
  } else if (hasCountValue(meta, 'all_count')) {
    filteredAllCount = sanitizeCount(meta.all_count);
  }

  return {
    mineCount: hasCountValue(meta, 'mine_count')
      ? sanitizeCount(meta.mine_count)
      : existingCount(state, 'mineCount'),
    unAssignedCount: hasCountValue(meta, 'unassigned_count')
      ? sanitizeCount(meta.unassigned_count)
      : existingCount(state, 'unAssignedCount'),
    allCount: hasCountValue(meta, 'all_count')
      ? sanitizeCount(meta.all_count)
      : existingCount(state, 'allCount'),
    filteredAllCount,
    unreadCount: hasCountValue(meta, 'unread_count')
      ? sanitizeCount(meta.unread_count)
      : existingCount(state, 'unreadCount'),
    unreadCountByInbox: hasCountValue(meta, 'unread_count_by_inbox')
      ? sanitizeInboxCounts(
          meta.unread_count_by_inbox,
          state.unreadCountByInbox
        )
      : state.unreadCountByInbox || {},
  };
};

const notifyConversationStatsUpdated = (
  windowObject = typeof window !== 'undefined' ? window : null
) => {
  if (typeof windowObject?.updateConversationStats !== 'function') {
    return false;
  }

  windowObject.updateConversationStats();
  return true;
};

export {
  buildConversationStatsUpdate,
  buildAssigneeTabItems,
  notifyConversationStatsUpdated,
  resolveConversationListTotal,
  sanitizeCount,
  sanitizeInboxCounts,
};
